import React, { useEffect } from "react";
import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Outlet, useLocation } from "react-router-dom";
import theme, { tokens } from "../theme/theme";
import SiteHeader from "./components/SiteHeader";
import SiteFooter from "./components/SiteFooter";
import MaintenancePage from "./pages/MaintenancePage";
import { SiteConfigProvider, useSiteConfig } from "./SiteConfigContext";
import { CHRISTMAS, HALLOWEEN, applySeasonToDocument } from "./halloween/season";
import HalloweenOverlay from "./halloween/HalloweenOverlay";
import Snowfall from "./christmas/Snowfall";

const TITLES = [
  { match: /^\/katalog/, title: "Каталог квартир — LeoCity" },
  { match: /^\/kvartyra/, title: "Квартира в оренду — LeoCity" },
  { match: /^\/View/, title: "Квартира в оренду — LeoCity" },
  { match: /^\/krosvord/, title: "Кросворд дня — LeoCity" },
  { match: /^\/kontakty/, title: "Контакти — LeoCity" },
];

const DEFAULT_TITLE = "LeoCity — оренда квартир у Львові";

/**
 * The only two routes that exist to list apartments. While maintenance mode is
 * on they are replaced by MaintenancePage; everything else — a shared
 * /kvartyra/<id> link, the crossword, contacts — keeps working normally.
 */
const LISTING_ROUTES = [/^\/$/, /^\/katalog(\/|$)/];

/** Neutral hold while the flag is being read, so listings never flash. */
function BootScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: tokens.canvas,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress
        size={30}
        sx={{
          color: tokens.blue600,
          // Held invisible for a beat: on a normal load the flag arrives before
          // this fires, and a spinner that flashes for 80ms looks like a glitch.
          opacity: 0,
          "@keyframes leocityBootFade": { to: { opacity: 1 } },
          animation: "leocityBootFade .2s ease .4s forwards",
        }}
      />
    </Box>
  );
}

/**
 * Layout for every public (non-admin) page: header, routed content, footer.
 * Scroll position and document title are reset on navigation.
 */
function PublicSiteBody() {
  const location = useLocation();
  const { maintenance, ready } = useSiteConfig();

  const isListingRoute = LISTING_ROUTES.some(function (route) {
    return route.test(location.pathname);
  });

  /**
   * Cognito's Hosted UI returns to "/" with `#id_token=...` in the URL, and
   * CognitoTokenGate in App.js forwards it to /admin — but only if that route
   * actually renders. Maintenance mode must never swallow a realtor login,
   * which is the one thing this page exists to offer.
   */
  const cognitoRedirect = window.location.href.indexOf("id_token") !== -1;
  const gated = isListingRoute && !cognitoRedirect;
  const hidden = gated && ready && maintenance;

  useEffect(
    function () {
      window.scrollTo(0, 0);
      if (hidden) {
        document.title = DEFAULT_TITLE;
        return;
      }
      const hit = TITLES.filter(function (t) {
        return t.match.test(location.pathname);
      })[0];
      document.title = hit ? hit.title : DEFAULT_TITLE;
    },
    [location.pathname, hidden]
  );

  if (gated && !ready) return <BootScreen />;
  if (hidden) {
    return (
      <React.Fragment>
        <MaintenancePage />
        {HALLOWEEN && <HalloweenOverlay />}
        {/* Outline-free flakes: the whole page is night sky. */}
        {CHRISTMAS && <Snowfall soft />}
      </React.Fragment>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <SiteHeader />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>
      <SiteFooter />
      {HALLOWEEN && <HalloweenOverlay />}
      {/* Outlined flakes, which stay visible over the white pages too — and
          half as many: in front of listings and text a full fall is clutter. */}
      {CHRISTMAS && <Snowfall density={0.5} />}
    </Box>
  );
}

export default function PublicSite() {
  // Seasonal font, page-wide colours and phone chrome — undone if the visitor
  // moves on to the admin panel, which shares this document.
  useEffect(applySeasonToDocument, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SiteConfigProvider>
        <PublicSiteBody />
      </SiteConfigProvider>
    </ThemeProvider>
  );
}
